import React, { useMemo } from 'react';
import { SidePanelPetServiceRemindersFragment } from 'shared/types/graphql';
import { DetailViewTabName, ShowDetailView } from '../../../shared/types/enum/Mode';
import { Box, Center, HStack, Tag, Text, Button, Flex } from '@televet/kibble-ui';
import { parseISO, format } from 'date-fns';

interface ISidePanelPetServicesProps {
  onGetPetServiceDue: SidePanelPetServiceRemindersFragment[];
  isSidePanelPetProfileLoading: boolean;
  onShowDetailView: React.Dispatch<React.SetStateAction<ShowDetailView>>;
  showDetailView: ShowDetailView;
  currentLoadCount: number;
  onSetCurrentLoadCount: React.Dispatch<React.SetStateAction<number>>;
  onSetTabName: React.Dispatch<React.SetStateAction<DetailViewTabName>>;
}

const SidePanelPetServices = ({
  onGetPetServiceDue,
  isSidePanelPetProfileLoading,
  onShowDetailView,
  showDetailView,
  currentLoadCount,
  onSetCurrentLoadCount,
  onSetTabName,
}: ISidePanelPetServicesProps): JSX.Element => {
  const currentDate = useMemo(() => new Date().valueOf(), []);

  const onShowLoadMore = useMemo(
    () =>
      showDetailView !== ShowDetailView.None &&
      onGetPetServiceDue?.length > 20 &&
      currentLoadCount < onGetPetServiceDue?.length,
    [currentLoadCount, onGetPetServiceDue?.length, showDetailView],
  );

  return onGetPetServiceDue?.length > 0 ? (
    <Box>
      {onGetPetServiceDue?.slice(0, currentLoadCount).map((serviceDue) => {
        return (
          <Box
            my={3}
            p={4}
            height="auto"
            maxH={40}
            border="1px"
            borderRadius="md"
            borderColor="border.default"
            key={serviceDue.id}
            alignItems="flex-end"
          >
            <HStack justify="space-between">
              <Text size="sm" fontWeight="bold">
                {serviceDue.service.name}
              </Text>
              <Text size="xs" variant="accent">
                {format(new Date(serviceDue.dueDate), 'MM/dd/yy ')}
              </Text>
            </HStack>
            { !serviceDue.isCompleted &&
              parseISO(serviceDue.dueDate).valueOf() < currentDate &&
              (!serviceDue.dateLastGiven || parseISO(serviceDue.dateLastGiven).valueOf() < currentDate) && (
                <Flex justify="flex-end" mt={2}>
                  <Tag label="Overdue" size="sm" background="background.dangerContrast" color="text.contrast" />
                </Flex>
              )}
          </Box>
        );
      })}
      {onShowLoadMore && (
        <Button
          width="100%"
          variant="tertiary"
          size="sm"
          onClick={(): void => onSetCurrentLoadCount((prev) => prev + 20)}
        >
          Load More
        </Button>
      )}
      {showDetailView === ShowDetailView.None && onGetPetServiceDue?.length > 2 && (
        <Button
          width="100%"
          variant="tertiary"
          size="sm"
          onClick={(): void => {
            onShowDetailView(ShowDetailView.ServicesDue);
            onSetTabName(DetailViewTabName.Active);
            onSetCurrentLoadCount(20);
          }}
        >
          View All Services Due
        </Button>
      )}
    </Box>
  ) : (
    <Center my={3}>
      <Text variant="subtle">{isSidePanelPetProfileLoading ? 'Loading...' : 'No upcoming services'}</Text>
    </Center>
  );
};

export default SidePanelPetServices;
